/**
 * Enhanced sync hook that includes both syncService and offlineReviewService stats
 *
 * This hook provides comprehensive sync status including:
 * - Pending actions from syncService queue
 * - Pending/failed responses from offlineReviewService SQLite
 * - Total pending items across both systems
 */

import { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../store';
import { selectOfflineSyncSummary } from '../store/slices/offlineSlice';
import { syncService } from '../services/syncService';
import { getOfflineReviewService, getPhotoQueueService } from '../services/platformServices';
import { shouldUseOfflineReviewWorkflow } from '../config/offlineWorkflow';
import type { RecoverableWorkItem } from '../services/offlineDatabase.types';

const offlineReviewService = getOfflineReviewService();
const photoQueueService = getPhotoQueueService();

interface PhotoRecoveryCounts {
  photosUploaded: number;
  photosStillFailed: number;
  photosMissing: number;
}

interface SelectedWorkRecoveryResult extends PhotoRecoveryCounts {
  recovered: number;
  skipped: number;
  failed: number;
}

interface OfflineSyncStats {
  // Network status
  isOnline: boolean;

  // Sync progress
  syncInProgress: boolean;

  // Pending counts from syncService queue
  pendingActionsCount: number;

  // Pending counts from offlineReviewService SQLite
  pendingResponses: number;
  failedResponses: number;
  pendingPhotos: number;
  failedPhotos: number;
  availableLocalPhotos: number;
  missingLocalPhotos: number;
  pendingOnDemandItems: number;
  pendingReviews: number;
  recoverableItems: number;
  recoveryAvailable: boolean;
  recoveryWorkItems: RecoverableWorkItem[];

  // Combined total
  waitingToSyncCount: number;
  retryNeededCount: number;
  totalPending: number;

  // Timing
  lastSyncTime: Date | null;
  lastSyncStatus: 'success' | 'error' | 'pending' | null;

  // Actions
  forceSync: () => Promise<PhotoRecoveryCounts | void>;
  refreshStats: () => Promise<void>;
  refreshRecoveryWorkItems: () => Promise<void>;
  recoverSelectedWorkItems: (itemIds: string[]) => Promise<SelectedWorkRecoveryResult>;
  keepServerForSelectedWorkItems: (itemIds: string[]) => Promise<{ keptServer: number; skipped: number; failed: number }>;
}

export const useOfflineSync = (): OfflineSyncStats => {
  const offlineSync = useSelector((state: RootState) => selectOfflineSyncSummary(state), shallowEqual);
  const [lastSyncStatus, setLastSyncStatus] = useState<'success' | 'error' | 'pending' | null>(null);
  const [offlineStats, setOfflineStats] = useState({
    pendingResponses: 0,
    failedResponses: 0,
    pendingPhotos: 0,
    failedPhotos: 0,
    availableLocalPhotos: 0,
    missingLocalPhotos: 0,
    pendingOnDemandItems: 0,
    pendingReviews: 0,
    recoverableItems: 0,
    recoveryAvailable: false,
    waitingToSyncCount: 0,
    retryNeededCount: 0,
    totalPending: 0,
  });
  const [recoveryWorkItems, setRecoveryWorkItems] = useState<RecoverableWorkItem[]>([]);

  const refreshRecoveryWorkItems = async () => {
    if (Platform.OS === 'web' || !offlineReviewService) {
      setRecoveryWorkItems([]);
      return;
    }

    try {
      if (offlineReviewService.isServiceInitialized && offlineReviewService.isServiceInitialized()) {
        const items = await offlineReviewService.getRecoverableWorkItems?.();
        setRecoveryWorkItems(Array.isArray(items) ? items : []);
      }
    } catch (error) {
      console.warn('[useOfflineSync] Failed to load recovery work items:', error);
      setRecoveryWorkItems([]);
    }
  };

  // Refresh offline review service stats
  const refreshStats = async () => {
    if (Platform.OS === 'web' || !offlineReviewService) {
      return;
    }

    try {
      if (offlineReviewService.isServiceInitialized && offlineReviewService.isServiceInitialized()) {
        const [stats, localFileStats] = await Promise.all([
          offlineReviewService.getSyncStats(),
          photoQueueService?.getLocalFileStats?.().catch(() => ({ available: 0, missing: 0 })) ??
            Promise.resolve({ available: 0, missing: 0 }),
        ]);
        const nextStats = {
          pendingResponses: stats.pendingResponses,
          failedResponses: stats.failedResponses,
          pendingPhotos: stats.pendingPhotos || 0,
          failedPhotos: stats.failedPhotos || 0,
          availableLocalPhotos: localFileStats.available || 0,
          missingLocalPhotos: localFileStats.missing || 0,
          pendingOnDemandItems: stats.pendingOnDemandItems || 0,
          pendingReviews: stats.pendingReviews,
          recoverableItems: stats.recoverableItems || 0,
          recoveryAvailable: Boolean(stats.recoveryAvailable),
          waitingToSyncCount: stats.waitingToSyncCount ?? (
            stats.pendingReviews +
            stats.pendingResponses +
            (stats.pendingPhotos || 0) +
            (stats.pendingOnDemandItems || 0)
          ),
          retryNeededCount: stats.retryNeededCount ?? (
            stats.failedResponses + (stats.failedPhotos || 0)
          ),
          totalPending: stats.totalPending ?? (
            stats.pendingReviews +
            stats.pendingResponses +
            (stats.pendingPhotos || 0) +
            (stats.pendingOnDemandItems || 0) +
            stats.failedResponses +
            (stats.failedPhotos || 0)
          ),
        };
        setOfflineStats(nextStats);
        if (nextStats.recoveryAvailable) {
          await refreshRecoveryWorkItems();
        } else {
          setRecoveryWorkItems([]);
        }
      }
    } catch (error) {
      console.warn('[useOfflineSync] Failed to get offline stats:', error);
    }
  };

  // Initialize and load stats on mount
  useEffect(() => {
    refreshStats();

    // Refresh stats periodically
    const interval = setInterval(refreshStats, 5000); // Every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Monitor sync progress changes
  useEffect(() => {
    if (offlineSync.syncInProgress) {
      setLastSyncStatus('pending');
    } else if (offlineSync.lastSyncTime) {
      setLastSyncStatus('success');
      // Refresh stats after sync completes
      refreshStats();
    }
  }, [offlineSync.syncInProgress, offlineSync.lastSyncTime]);

  const forceSync = async () => {
    try {
      setLastSyncStatus('pending');
      let photoRecoveryResult: PhotoRecoveryCounts | void;

      if (shouldUseOfflineReviewWorkflow()) {
        photoRecoveryResult = await syncService.forcSync();
      } else {
        // Trigger both sync systems; queue sync errors shouldn't block offline DB sync
        try {
          await syncService.forcSync();
        } catch (queueError) {
          console.warn('[useOfflineSync] Queue sync failed, continuing with offline sync:', queueError);
        }

        if (Platform.OS !== 'web' && offlineReviewService?.performFullSync) {
          photoRecoveryResult = offlineReviewService.retryRecoverySync
            ? await offlineReviewService.retryRecoverySync()
            : await offlineReviewService.performFullSync();
        }
      }

      setLastSyncStatus('success');
      await refreshStats();
      await refreshRecoveryWorkItems();
      return photoRecoveryResult;
    } catch (error) {
      setLastSyncStatus('error');
      throw error;
    }
  };

  const recoverSelectedWorkItems = async (itemIds: string[]) => {
    if (Platform.OS === 'web' || !offlineReviewService?.recoverSelectedWorkItemIds) {
      return {
        recovered: 0,
        skipped: itemIds.length,
        failed: 0,
        photosUploaded: 0,
        photosStillFailed: 0,
        photosMissing: 0,
      };
    }

    const result = await offlineReviewService.recoverSelectedWorkItemIds(itemIds);
    await refreshStats();
    await refreshRecoveryWorkItems();
    return result;
  };

  const keepServerForSelectedWorkItems = async (itemIds: string[]) => {
    if (Platform.OS === 'web' || !offlineReviewService?.keepServerForSelectedWorkItemIds) {
      return { keptServer: 0, skipped: itemIds.length, failed: 0 };
    }

    const result = await offlineReviewService.keepServerForSelectedWorkItemIds(itemIds);
    await refreshStats();
    await refreshRecoveryWorkItems();
    return result;
  };

  const waitingToSyncCount =
    offlineSync.pendingActionsCount + offlineStats.waitingToSyncCount;
  const retryNeededCount = offlineStats.retryNeededCount;
  const totalPending = offlineSync.pendingActionsCount + offlineStats.totalPending;

  return {
    isOnline: offlineSync.isOnline,
    syncInProgress: offlineSync.syncInProgress,
    pendingActionsCount: offlineSync.pendingActionsCount,
    pendingResponses: offlineStats.pendingResponses,
    failedResponses: offlineStats.failedResponses,
    pendingPhotos: offlineStats.pendingPhotos,
    failedPhotos: offlineStats.failedPhotos,
    availableLocalPhotos: offlineStats.availableLocalPhotos,
    missingLocalPhotos: offlineStats.missingLocalPhotos,
    pendingOnDemandItems: offlineStats.pendingOnDemandItems,
    pendingReviews: offlineStats.pendingReviews,
    recoverableItems: offlineStats.recoverableItems,
    recoveryAvailable: offlineStats.recoveryAvailable,
    recoveryWorkItems,
    waitingToSyncCount,
    retryNeededCount,
    totalPending,
    lastSyncTime: offlineSync.lastSyncTime ? new Date(offlineSync.lastSyncTime) : null,
    lastSyncStatus,
    forceSync,
    refreshStats,
    refreshRecoveryWorkItems,
    recoverSelectedWorkItems,
    keepServerForSelectedWorkItems,
  };
};
